import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertTriangle,
  ArrowLeft,
  Search,
  Loader2,
  CheckCircle2,
  RotateCcw,
  ShieldAlert,
  History,
  Copy,
  Info,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Admin page wrapping POST /api/oos-remediation/{preview,apply,revert} and
 * GET /api/oos-remediation/runs. Replaces the SSH-only CLI for non-technical
 * operators. All actions are super_admin / admin only and audit-logged.
 */
export default function OOSRemediation() {
  const navigate = useNavigate();
  const [limit, setLimit] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lastApplyResult, setLastApplyResult] = useState(null);
  const [runs, setRuns] = useState([]);
  const [revertingId, setRevertingId] = useState(null);
  const [revertTarget, setRevertTarget] = useState(null);

  const loadRuns = useCallback(async () => {
    try {
      const { data } = await api.get('/oos-remediation/runs');
      setRuns(data.runs || []);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  const handlePreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const payload = limit ? { limit: parseInt(limit, 10) } : {};
      const { data } = await api.post('/oos-remediation/preview', payload);
      setPreview(data);
      if (data.total_lines === 0) {
        toast.info('No matching lines found. Nothing to remediate.');
      } else {
        toast.success(`Found ${data.total_lines} line(s) across ${data.total_dispatches} dispatch(es).`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Preview failed');
    } finally {
      setPreviewing(false);
    }
  };

  const handleApply = async () => {
    setApplying(true);
    setConfirmOpen(false);
    try {
      const payload = limit ? { limit: parseInt(limit, 10) } : {};
      const { data } = await api.post('/oos-remediation/apply', payload);
      setLastApplyResult(data);
      setPreview(null);
      toast.success(`Flagged ${data.total_lines} line(s). Run ID saved.`);
      await loadRuns();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Apply failed');
    } finally {
      setApplying(false);
    }
  };

  const handleRevert = async (runId) => {
    setRevertingId(runId);
    setRevertTarget(null);
    try {
      const { data } = await api.post(`/oos-remediation/revert/${runId}`);
      toast.success(`Reverted ${data.reverted_lines} line(s) on ${data.reverted_dispatches} dispatch(es).`);
      await loadRuns();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Revert failed');
    } finally {
      setRevertingId(null);
    }
  };

  const copyRunId = (runId) => {
    navigator.clipboard.writeText(runId).then(
      () => toast.success('Run ID copied'),
      () => toast.error('Copy failed — select the ID manually'),
    );
  };

  return (
    <div className="space-y-6" data-testid="oos-remediation-page">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/data-management')} data-testid="btn-back-to-dm">
          <ArrowLeft className="w-4 h-4 mr-1" />
          Data Management
        </Button>
      </div>

      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <ShieldAlert className="w-6 h-6 text-amber-600" />
          OOS Flag Bulk Remediation
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Retroactively re-flag dispatch lines that lost their <strong>Seasonally Out Of Stock</strong> marker
          during the Feb-2026 Pydantic-model-drop bug. Preview before applying; every apply is reversible.
        </p>
      </div>

      {/* What this does */}
      <Card className="border-blue-200 bg-blue-50/40">
        <CardContent className="pt-5 pb-4 text-sm space-y-2">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-blue-900 space-y-1">
              <p><strong>Matches:</strong> Locked / Ready / Dispatched orders that contain a line with <strong>no batch attached</strong>, <strong>no weight</strong>, and <strong>no cases</strong>, but still has a positive ordered quantity. That's the exact fingerprint left behind by the OOS-save bug — the ordered units were preserved on the line but the batch/weight/cases fields were nulled and the OOS flag was silently dropped by Pydantic.</p>
              <p><strong>Skipped:</strong> Drafts, In-Progress, soft-deleted orders, and any line that already has a batch code OR any weight/case value OR is already flagged OOS.</p>
              <p><strong>Reversible:</strong> Every Apply gets a unique <code>run_id</code>; click Revert next to any past run to undo it cleanly (lines re-flagged manually after a run are never touched).</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Controls */}
      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="w-4 h-4" />
            Preview & Apply
          </CardTitle>
          <CardDescription>Step 1 — run a Preview. Step 2 — review the matches table. Step 3 — Apply.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs">Limit (optional)</Label>
              <Input
                type="number"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                placeholder="No limit"
                min={1}
                max={5000}
                className="w-32 h-9"
                data-testid="input-limit"
              />
            </div>
            <Button onClick={handlePreview} disabled={previewing} data-testid="btn-preview">
              {previewing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              Preview
            </Button>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={!preview || preview.total_lines === 0 || applying}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="btn-apply"
            >
              {applying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Apply ({preview?.total_lines || 0} lines)
            </Button>
          </div>

          {/* Last apply result banner */}
          {lastApplyResult && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm flex items-start gap-2" data-testid="apply-result">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-emerald-900">
                  Flagged <strong>{lastApplyResult.total_lines}</strong> line(s) on <strong>{lastApplyResult.total_dispatches}</strong> dispatch(es).
                </p>
                <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1.5">
                  Run ID: <code className="bg-white px-1.5 py-0.5 rounded border">{lastApplyResult.run_id}</code>
                  <Button variant="ghost" size="sm" className="h-5 px-1" onClick={() => copyRunId(lastApplyResult.run_id)}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </p>
              </div>
            </div>
          )}

          {/* Preview table */}
          {preview && (
            <div className="border rounded-md overflow-hidden">
              <div className="px-3 py-2 bg-muted/40 text-xs flex items-center justify-between">
                <span>
                  Showing <strong>{preview.total_lines}</strong> line(s) across <strong>{preview.total_dispatches}</strong> dispatch(es).
                </span>
                {preview.total_lines > 0 && (
                  <span className="text-amber-700">↓ Review carefully before clicking Apply</span>
                )}
              </div>
              {preview.total_lines === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No matching lines. Your data is clean for this bug.
                </div>
              ) : (
                <div className="max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm" data-testid="preview-table">
                    <thead className="sticky top-0 bg-amber-50 z-10">
                      <tr className="border-b text-amber-900">
                        <th className="px-3 py-2 text-left font-semibold">Dispatch #</th>
                        <th className="px-3 py-2 text-left font-semibold">PO</th>
                        <th className="px-3 py-2 text-left font-semibold">Customer</th>
                        <th className="px-3 py-2 text-left font-semibold">Date</th>
                        <th className="px-3 py-2 text-left font-semibold">Status</th>
                        <th className="px-3 py-2 text-left font-semibold">Product</th>
                        <th className="px-3 py-2 text-right font-semibold">Qty</th>
                        <th className="px-3 py-2 text-right font-semibold">Cases</th>
                        <th className="px-3 py-2 text-right font-semibold">Weight</th>
                        <th className="px-3 py-2 text-left font-semibold">Batch</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.matches.map((m, idx) => (
                        <tr key={`${m.dispatch_id}-${m.line_index}`} className="border-b hover:bg-amber-50/40" data-testid={`preview-row-${idx}`}>
                          <td className="px-3 py-1.5 font-mono">{m.dispatch_number}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{m.po_number || '—'}</td>
                          <td className="px-3 py-1.5">{m.customer_name}</td>
                          <td className="px-3 py-1.5">{m.dispatch_date || '—'}</td>
                          <td className="px-3 py-1.5 capitalize">{(m.status || '').replace('_', ' ')}</td>
                          <td className="px-3 py-1.5">{m.product_name}</td>
                          <td className="px-3 py-1.5 text-right font-mono">{m.quantity ?? '—'}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-muted-foreground">{m.cases ?? '—'}</td>
                          <td className="px-3 py-1.5 text-right font-mono text-muted-foreground">{m.weight ?? '—'}</td>
                          <td className="px-3 py-1.5 text-xs text-muted-foreground">{m.batch_code || <span className="italic">none</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Previous runs */}
      <Card>
        <CardHeader className="py-4 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4" />
              Previous Runs
            </CardTitle>
            <CardDescription>Every Apply produces an audit-logged, reversible run.</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={loadRuns} data-testid="btn-refresh-runs">
            <RotateCcw className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent>
          {runs.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground" data-testid="runs-empty">
              No remediation runs recorded for this tenant.
            </div>
          ) : (
            <div className="border rounded-md overflow-hidden">
              <table className="w-full text-sm" data-testid="runs-table">
                <thead>
                  <tr className="border-b bg-muted/40">
                    <th className="px-3 py-2 text-left font-semibold">Run ID</th>
                    <th className="px-3 py-2 text-left font-semibold">Applied At</th>
                    <th className="px-3 py-2 text-center font-semibold">Dispatches</th>
                    <th className="px-3 py-2 text-center font-semibold">Lines</th>
                    <th className="px-3 py-2 text-right font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.run_id} className="border-b hover:bg-muted/30" data-testid={`run-row-${r.run_id}`}>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <code className="text-xs">{r.run_id}</code>
                          <Button variant="ghost" size="sm" className="h-5 px-1" onClick={() => copyRunId(r.run_id)} title="Copy run ID">
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {r.applied_at ? new Date(r.applied_at).toLocaleString('en-IE') : '—'}
                      </td>
                      <td className="px-3 py-2 text-center">{r.total_dispatches}</td>
                      <td className="px-3 py-2 text-center">{r.total_lines}</td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRevertTarget(r)}
                          disabled={revertingId === r.run_id}
                          data-testid={`btn-revert-${r.run_id}`}
                          className="border-red-200 text-red-700 hover:bg-red-50 gap-1"
                        >
                          {revertingId === r.run_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                          Revert
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Apply confirmation dialog */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="dialog-confirm-apply">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Apply Remediation?
            </DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <span className="block">
                This will flag <strong>{preview?.total_lines || 0}</strong> line(s) on <strong>{preview?.total_dispatches || 0}</strong> dispatch(es) as Seasonally Out Of Stock.
              </span>
              <span className="block text-amber-700">
                A unique <code>run_id</code> is generated so you can revert this entire batch later. Every apply is audit-logged.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleApply} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-confirm-apply">
              {applying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
              Yes, Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revert confirmation dialog */}
      <Dialog open={!!revertTarget} onOpenChange={(o) => !o && setRevertTarget(null)}>
        <DialogContent data-testid="dialog-confirm-revert">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-red-600" />
              Revert Run?
            </DialogTitle>
            <DialogDescription className="space-y-2 pt-2">
              <span className="block">
                This will <strong>unset out_of_stock</strong> on every line stamped by run <code className="text-xs">{revertTarget?.run_id}</code>.
              </span>
              <span className="block text-red-700">
                Lines that were re-flagged manually after this run are protected and will not be touched.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevertTarget(null)}>Cancel</Button>
            <Button
              onClick={() => handleRevert(revertTarget.run_id)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="btn-confirm-revert"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Yes, Revert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
